import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

const BellNotificationContext = createContext();

export const useBellNotifications = () => {
  const context = useContext(BellNotificationContext);
  if (!context) {
    throw new Error('useBellNotifications must be used within a BellNotificationProvider');
  }
  return context;
};

export const BellNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef(new Map());

  const removeNotification = useCallback((id) => {
    const timerId = timersRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timersRef.current.delete(id);
    }
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const addNotification = useCallback((payload = {}) => {
    const notification = {
      id: payload.id || `bell-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: payload.type || 'system',
      convId: payload.convId ?? null,
      title: payload.title || 'Notificacion del sistema',
      subtitle: payload.subtitle || '',
      actionLabel: payload.actionLabel || '',
      onAction: typeof payload.onAction === 'function' ? payload.onAction : null,
      // Varios botones en el mismo aviso: [{ label, onClick, color }].
      //
      // Existe porque hay avisos que no son "ve a mirar esto" sino una decision
      // con dos salidas —aprobar o rechazar— y obligar a abrir otra pantalla
      // para pulsar una de las dos convierte un gesto de dos segundos en un
      // viaje. `actionLabel` se queda para lo que ya lo usaba.
      actions: Array.isArray(payload.actions)
        ? payload.actions.filter((accion) => accion && typeof accion.onClick === 'function')
        : [],
      // Visto no es lo mismo que resuelto.
      //
      // El contador rojo dice "hay algo que no sabes". En cuanto se abre la
      // campana, ya se sabe, y seguir contandolo es dar la lata por algo que la
      // persona acaba de leer. La notificacion se queda —hay que aprobarla o
      // rechazarla—, pero el numero se apaga.
      seen: false,
      createdAt: Date.now()
    };
    // Un aviso que se actualiza (p. ej. "en cola" -> "procesando") puede
    // conservar su estado de visto para no volver a encender el contador.
    const keepSeen = payload.preserveSeen === true;

    // De-duplicate by id (e.g. repeated chat notifications keep newest position)
    const existingTimer = timersRef.current.get(notification.id);
    if (existingTimer) {
      clearTimeout(existingTimer);
      timersRef.current.delete(notification.id);
    }
    setNotifications((prev) => {
      const previous = prev.find((item) => item.id === notification.id);
      const next = keepSeen && previous ? { ...notification, seen: previous.seen } : notification;
      return [next, ...prev.filter((item) => item.id !== notification.id)].slice(0, 20);
    });

    // Un aviso que pide una decision no puede evaporarse a los veinte segundos:
    // el que tenia que decidir estaba mirando otra cosa y se queda sin enterarse
    // de que alguien espera. Se queda hasta que lo resuelvan o lo cierren.
    if (payload.persistente === true) return notification.id;

    const ttl = Number(payload.duration) > 0 ? Number(payload.duration) : 20000;
    const timerId = window.setTimeout(() => {
      removeNotification(notification.id);
    }, ttl);
    timersRef.current.set(notification.id, timerId);

    return notification.id;
  }, [removeNotification]);

  /** Se abrio la campana: todo lo que hay dentro ya esta leido. */
  const markAllSeen = useCallback(() => {
    setNotifications((prev) => (
      prev.some((item) => !item.seen)
        ? prev.map((item) => (item.seen ? item : { ...item, seen: true }))
        : prev   // sin cambios reales, sin repintado
    ));
  }, []);

  const clearNotifications = useCallback(() => {
    timersRef.current.forEach((timerId) => clearTimeout(timerId));
    timersRef.current.clear();
    setNotifications([]);
  }, []);

  // Remove all notifications matching a predicate (e.g. dismiss chat
  // notifications for a conversation once it has been read).
  const removeNotificationsBy = useCallback((predicate) => {
    if (typeof predicate !== 'function') return;
    setNotifications((prev) => {
      prev.forEach((item) => {
        if (predicate(item)) {
          const timerId = timersRef.current.get(item.id);
          if (timerId) {
            clearTimeout(timerId);
            timersRef.current.delete(item.id);
          }
        }
      });
      return prev.filter((item) => !predicate(item));
    });
  }, []);

  const value = useMemo(() => ({
    notifications,
    // Lo que cuenta el globo rojo: solo lo que la persona todavia no ha visto.
    unseenCount: notifications.filter((item) => !item.seen).length,
    addNotification,
    removeNotification,
    removeNotificationsBy,
    markAllSeen,
    clearNotifications
  }), [notifications, addNotification, removeNotification, removeNotificationsBy, markAllSeen, clearNotifications]);

  return (
    <BellNotificationContext.Provider value={value}>
      {children}
    </BellNotificationContext.Provider>
  );
};

export default BellNotificationContext;
